import React, { useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Table,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  useAssignCommandCentreJob,
  useCommandCentreAssignableJobs,
} from '@/api/CommandCentre';
import { useJobTypes } from '@/api/Jobs';
import type { AssignableJob, AssignableJobsCreatedSort } from '@/types';
import { useTranslate } from '@/translations';
import { timeConverter } from '@/utils/helpers';

interface JobAssignmentModalProps {
  open: boolean;
  operatorId: number;
  operatorDisplayName: string;
  siteId: number;
  onClose(): void;
  onAssigned(): void;
}

const getProblemDetail = (message: unknown): string | undefined => {
  if (typeof message === 'string') return message;
  if (!message || typeof message !== 'object') return undefined;

  const problem = message as {
    detail?: unknown;
    title?: unknown;
    Message?: unknown;
  };
  for (const candidate of [problem.detail, problem.Message, problem.title]) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
  }

  return undefined;
};

export const JobAssignmentModal = ({
  open,
  operatorId,
  operatorDisplayName,
  siteId,
  onClose,
  onAssigned,
}: JobAssignmentModalProps) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [draftSearch, setDraftSearch] = useState('');
  const [search, setSearch] = useState('');
  const [jobType, setJobType] = useState<string>();
  const [createdSort, setCreatedSort] =
    useState<AssignableJobsCreatedSort>('desc');
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<AssignableJob>();
  const [conflictMessage, setConflictMessage] = useState<string>();
  const { data, isFetching, refetch } = useCommandCentreAssignableJobs(
    siteId,
    search,
    jobType,
    createdSort,
    page,
    open,
  );
  const { data: jobTypes, isFetching: areJobTypesLoading } = useJobTypes();
  const { mutateAsync: assignJob, isPending } = useAssignCommandCentreJob();

  useEffect(() => {
    if (!open) return;
    setDraftSearch('');
    setSearch('');
    setJobType(undefined);
    setCreatedSort('desc');
    setPage(1);
    setSelectedJob(undefined);
    setConflictMessage(undefined);
  }, [open, operatorId, siteId]);

  const confirmAssignment = async () => {
    if (!selectedJob) return;

    try {
      const result = await assignJob({
        userId: operatorId,
        jobId: selectedJob.jobId,
        siteId,
        expectedOwnerId: selectedJob.currentOwnerId,
      });

      if (result.success) {
        message.success(t('ccJobAssignedSuccess'));
        setSelectedJob(undefined);
        onAssigned();
        void refetch();
        return;
      }

      if (result.code === 409) {
        setSelectedJob(undefined);
        setConflictMessage(t('ccAssignmentConflict'));
        await refetch();
        return;
      }

      message.error(
        getProblemDetail(result.message) ?? t('ccAssignmentFailed'),
      );
    } catch {
      message.error(t('ccAssignmentFailed'));
    }
  };

  const getConfirmationText = (job: AssignableJob) =>
    job.currentOwnerDisplayName
      ? t('ccReassignConfirmation', {
          jobId: job.jobId,
          previousOwner: job.currentOwnerDisplayName,
          newOwner: operatorDisplayName,
        })
      : t('ccAssignConfirmation', {
          jobId: job.jobId,
          newOwner: operatorDisplayName,
        });

  const columns: ColumnsType<AssignableJob> = [
    { title: t('ccJobId'), dataIndex: 'jobId', width: 90 },
    { title: t('ccJobType'), dataIndex: 'jobType' },
    { title: t('ccVin'), dataIndex: 'vin' },
    { title: t('ccStatus'), dataIndex: 'status', width: 100 },
    {
      title: t('ccCreated'),
      dataIndex: 'createdAt',
      width: 160,
      sorter: true,
      sortDirections: ['descend', 'ascend', 'descend'],
      sortOrder: createdSort === 'desc' ? 'descend' : 'ascend',
      render: (createdAt: string) => timeConverter(createdAt),
    },
    {
      title: t('ccCurrentOwner'),
      dataIndex: 'currentOwnerDisplayName',
      render: (_, job) => job.currentOwnerDisplayName ?? '-',
    },
    {
      title: t('ccAction'),
      key: 'action',
      width: 110,
      render: (_, job) => {
        const isCurrentOwner = job.currentOwnerId === operatorId;
        const ariaLabel = isCurrentOwner
          ? t('ccAlreadyAssignedTo', { owner: operatorDisplayName })
          : job.currentOwnerId == null
            ? t('ccAssignJobAria', { jobId: job.jobId })
            : t('ccReassignJobAria', { jobId: job.jobId });

        return (
          <Popconfirm
            open={selectedJob?.jobId === job.jobId}
            placement="left"
            destroyOnHidden
            title={t('ccConfirmAssignment')}
            description={getConfirmationText(job)}
            okText={t('confirm')}
            okButtonProps={{ loading: isPending }}
            onConfirm={confirmAssignment}
            onCancel={() => setSelectedJob(undefined)}
            onOpenChange={(nextOpen) => {
              if (nextOpen) {
                setConflictMessage(undefined);
                setSelectedJob(job);
              } else if (!isPending) {
                setSelectedJob(undefined);
              }
            }}
          >
            <Button
              type="link"
              disabled={isCurrentOwner}
              aria-label={ariaLabel}
            >
              {isCurrentOwner ? t('ccAssigned') : t('ccAssign')}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      title={t('ccAssignJobTitle', { operator: operatorDisplayName })}
      styles={{
        body: {
          height: 'calc(100vh - 204px)',
          maxHeight: 600,
          overflowY: 'auto',
        },
      }}
    >
      {conflictMessage && (
        <Alert
          className="mb-3"
          type="warning"
          message={conflictMessage}
          showIcon
        />
      )}
      <div className="d-flex gap-2 mb-3">
        <Input.Search
          allowClear
          value={draftSearch}
          placeholder={t('ccSearchJobs')}
          style={{ flex: 1 }}
          onChange={(event) => setDraftSearch(event.target.value)}
          onSearch={(value) => {
            setSearch(value.trim());
            setPage(1);
            setConflictMessage(undefined);
          }}
        />
        <Select
          allowClear
          loading={areJobTypesLoading}
          value={jobType}
          placeholder={t('ccAllJobTypes')}
          style={{ width: 200 }}
          options={jobTypes?.map(({ text, value }) => ({
            label: text,
            value,
          }))}
          onChange={(value) => {
            setJobType(value);
            setPage(1);
            setConflictMessage(undefined);
          }}
        />
      </div>
      <Table<AssignableJob>
        size="small"
        loading={isFetching}
        dataSource={data?.items ?? []}
        columns={columns}
        pagination={false}
        rowKey="jobId"
        onChange={(_pagination, _filters, sorter) => {
          const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
          setCreatedSort(activeSorter.order === 'ascend' ? 'asc' : 'desc');
          setPage(1);
          setConflictMessage(undefined);
        }}
      />
      <Pagination
        className="mt-3"
        current={page}
        pageSize={10}
        total={data?.totalCount ?? 0}
        showTotal={(total) => t('ccTotalJobs', { total })}
        showSizeChanger={false}
        onChange={setPage}
      />
    </Modal>
  );
};
